import React, { createContext, useContext, useEffect, useState } from 'react'
import { useGetShopConfig } from '@/client/hooks/shopConfig'
import { IShopConfig } from '@/client/types/ShopConfig'
import { isPublicHoliday } from '@/client/utils/holiday'

type ConfigProviderProps = {
  children: React.ReactNode
}

// Type of the context value
type ConfigContextType = {
  shopConfig: IShopConfig | null
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

/**
 * Custom hook to return the ConfigContext.
 */
export const useConfig = () => {
  const context = useContext(ConfigContext)
  if (!!context) return context
  throw new Error(
    'useConfig must be used within a ConfigProvider. Did you forget to wrap your component in a <ConfigProvider />?'
  )
}

export enum ShopDomain {
  DE = 'etikett.de',
  GB = 'label.co.uk',
  FR = 'labelletiquette.fr',
  NL = 'unieketiket.nl',
  IT = 'etichetta.it',
  AU = 'primelabels.com.au',
  ES = 'etiqueta.es',
  US = 'labelmakers.com'
}

export const LOCALE_TO_SHOP_DOMAIN: Record<string, string> = {
  de: ShopDomain.DE,
  gb: ShopDomain.GB, // Not clear yet what will be the locale so we use two mappings for GB
  uk: ShopDomain.GB,
  fr: ShopDomain.FR,
  nl: ShopDomain.NL,
  it: ShopDomain.IT,
  au: ShopDomain.AU,
  es: ShopDomain.ES,
  us: ShopDomain.US
}

export const shopDomain = LOCALE_TO_SHOP_DOMAIN[process.env.NEXT_PUBLIC_DEFAULT_LOCALE || '']

/**
 * ConfigProvider component that provides the shopConfig to its children.
 * The shopConfig is fetched from the API using the useGetShopConfig hook with infinite stale time.
 *
 * @param children
 * @constructor
 */
export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [shopConfig, setShopConfig] = useState<IShopConfig | null>(null)
  const [nextWorkingDay, setNextWorkingDay] = useState<Date | null>(null)
  const [isHoliday, setIsHoliday] = useState<boolean>(false)

  const shopConfigApi = useGetShopConfig(shopDomain)

  useEffect(() => {
    // We enrich the data from API with some holiday data
    const countryRegion = (domain) =>
      // We just take the country from domain without a region
      ({ country: domain.substring(domain.lastIndexOf('.') + 1) })

    const today = new Date()
    if (shopDomain) {
      const country = countryRegion(shopDomain).country
      isPublicHoliday(today, country, true).then((info) => {
        if (info.isHoliday) {
          if (info.nextWorkingDay) {
            setNextWorkingDay(info.nextWorkingDay)
          }
        } else {
          setNextWorkingDay(null)
        }
        setIsHoliday(info.isHoliday)
      })
    }

    setShopConfig({
      domain: shopConfigApi.data?.domain || '',
      infoEmail: shopConfigApi.data?.infoEmail || '',
      timezone: shopConfigApi.data?.timezone || '',
      openingTimeFrom: shopConfigApi.data?.openingTimeFrom || '',
      openingTimeTo: shopConfigApi.data?.openingTimeTo || '',
      isHoliday: isHoliday,
      nextWorkingDay: nextWorkingDay || undefined
    })
  }, [shopConfigApi.data?.openingTimeFrom, shopConfigApi.data?.openingTimeTo])

  return <ConfigContext.Provider value={{ shopConfig }}>{children}</ConfigContext.Provider>
}
